// =========================================================================
// HelpyDoc · ICONOGRAFÍA OFICIAL
//
// Fuente de verdad ÚNICA del sistema de iconos: traduce cada concepto de la
// lámina de marca ("Iconografía oficial · HEALTH · NUTRICIÓN · BIENESTAR") al
// componente de `lucide-react` que lo representa. Las pantallas importan de
// aquí en vez de elegir icono por su cuenta, para que el mismo concepto se
// dibuje igual en toda la app.
//
// Especificación de la lámina: rejilla 24×24 · grosor 2 px · trazos
// redondeados · un solo icono por concepto. `ESPECIFICACION_ICONO` deja esos
// valores en código para no repetirlos suelto por las pantallas.
//
// Tres conceptos NO tienen equivalente exacto en lucide y se resuelven con el
// vecino semántico más cercano (anotado en cada línea): bíceps (proteínas),
// aguacate (grasas) y salero (sodio).
// =========================================================================
import {
  Activity,
  Apple,
  Bell,
  BookOpen,
  Brain,
  Calendar,
  Camera,
  Candy,
  ChefHat,
  CircleCheck,
  CircleHelp,
  CirclePlus,
  CircleX,
  ClipboardList,
  CupSoda,
  Download,
  Droplet,
  Droplets,
  Dumbbell,
  FlaskConical,
  Flame,
  Flower2,
  Footprints,
  Gauge,
  History,
  Home,
  Info,
  Leaf,
  Lightbulb,
  LoaderCircle,
  MessageCircle,
  Milk,
  MoonStar,
  Pencil,
  PersonStanding,
  Pill,
  Ruler,
  Salad,
  ScanLine,
  Search,
  Settings,
  Share2,
  Star,
  Target,
  Trash2,
  TrendingUp,
  TriangleAlert,
  User,
  Utensils,
  Users,
  Weight,
  Wheat,
  type LucideIcon,
} from 'lucide-react';

/** Rejilla, grosor y forma de trazo que fija la lámina de marca. */
export const ESPECIFICACION_ICONO = {
  tamano: 24,
  grosor: 2,
  remate: 'round',
} as const;

/** Navegación principal (la barra inferior y el menú lateral). */
export const ICONOS_NAVEGACION = {
  registrar: Camera,
  actividad: Footprints, // la lámina usa una zapatilla; Footprints es el equivalente de lucide
  animo: Brain,
  platos: Salad,
  informes: ClipboardList,
  perfil: User,
} as const satisfies Record<string, LucideIcon>;

/** Categorías principales. */
export const ICONOS_CATEGORIA = {
  inicio: Home,
  objetivos: Target,
  peso: Weight,
  medidas: Ruler,
  nutricion: Apple,
  habitos: CircleCheck,
  educacion: BookOpen,
  consejos: Lightbulb,
  recetas: ChefHat,
  favoritos: Star,
  progreso: TrendingUp,
  comunidad: Users,
  mensajes: MessageCircle,
  recordatorios: Bell,
  ajustes: Settings,
  ayuda: CircleHelp,
} as const satisfies Record<string, LucideIcon>;

/** Nutrición y macronutrientes. */
export const ICONOS_MACRO = {
  proteinas: Dumbbell, // la lámina usa un bíceps; lucide no lo tiene, Dumbbell mantiene la idea de fuerza
  carbohidratos: Wheat,
  grasas: Droplet, // la lámina usa un aguacate; sin equivalente en lucide, la gota de aceite es lo más cercano
  fibra: Leaf,
  azucares: Candy,
  sodio: Droplets, // la lámina usa un salero; sin equivalente en lucide
  agua: Droplet,
  calorias: Flame,
} as const satisfies Record<string, LucideIcon>;

/**
 * Categorías de alimento (`FoodCategory` de `DataService`).
 *
 * Se usan cuando un alimento no tiene fotografía utilizable, para enseñar una
 * imagen EQUIVALENTE a lo elegido en lugar del glifo de imagen rota del
 * navegador — ver `components/ImagenAlimento`. Las claves son exactamente las
 * de `FoodCategory`, incluida la tilde de «proteína»: el tipo obliga a
 * mantenerlas alineadas.
 */
export const ICONOS_ALIMENTO = {
  vegetal: Salad,
  'proteína': Dumbbell, // mismo criterio que ICONOS_MACRO.proteinas
  cereal: Wheat,
  'lácteo': Milk,
  fruta: Apple,
  grasa: Droplet, // igual que ICONOS_MACRO.grasas
  bebida: CupSoda,
  plato_combinado: ChefHat,
  snack: Candy,
  otro: Utensils,
} as const satisfies Record<string, LucideIcon>;

/** Salud y condiciones. */
export const ICONOS_SALUD = {
  diabetes: Droplet,
  obesidad: PersonStanding,
  corazon: Activity,
  presionArterial: Gauge,
  colesterol: Droplet,
  sueno: MoonStar,
  estres: Flower2,
  medicacion: Pill,
  examenes: FlaskConical,
  citas: Calendar,
} as const satisfies Record<string, LucideIcon>;

/** Acciones rápidas. */
export const ICONOS_ACCION = {
  anadirComida: CirclePlus,
  escanear: ScanLine,
  buscar: Search,
  historial: History,
  compartir: Share2,
  descargar: Download,
  editar: Pencil,
  eliminar: Trash2,
} as const satisfies Record<string, LucideIcon>;

/** Estados y feedback (los usa el sistema de avisos). */
export const ICONOS_ESTADO = {
  exito: CircleCheck,
  info: Info,
  advertencia: TriangleAlert,
  error: CircleX,
  cargando: LoaderCircle,
} as const satisfies Record<string, LucideIcon>;
